/**
 * Key parsing & normalization.
 *
 * Canonical form: "Mod+Shift+K", "Alt+n", "?", "ArrowUp", "g i" (chord).
 * Modifier order is canonical: Mod, Ctrl, Alt, Shift (Mod = ⌘ on macOS, Ctrl elsewhere).
 *
 * Keys themselves are normalized to:
 *   - Lowercase single-character letters: "a".."z"
 *   - Digits as-is: "0".."9"
 *   - Named keys preserved (KeyboardEvent.key): "ArrowUp", "Enter", "Escape", " " (Space)
 *   - Special tokens: "Space" (canonicalised from " "), "?" stays "?", "/" stays "/"
 *
 * "Mod" is platform-aware: on macOS it means Meta (⌘); elsewhere Control.
 */

export interface KeySignature {
  mod: boolean; // platform meta-mod (Cmd on Mac, Ctrl elsewhere)
  ctrl: boolean; // explicit Control (independent of Mod)
  alt: boolean;
  shift: boolean;
  key: string; // canonical key token (always lowercased for letters)
}

const MOD_TOKEN_RE = /^(mod|cmd|meta|command|super)$/i;
const CTRL_TOKEN_RE = /^(ctrl|control)$/i;
const ALT_TOKEN_RE = /^(alt|option|opt)$/i;
const SHIFT_TOKEN_RE = /^(shift)$/i;

const SPACE_KEY = " ";

function normalizeKeyToken(raw: string): string {
  if (raw === SPACE_KEY || raw.toLowerCase() === "space") {
    return "Space";
  }
  if (raw.length === 1) {
    return raw.toLowerCase();
  }
  // Preserve canonical KeyboardEvent.key names (Enter, Escape, Tab, ArrowUp, …)
  return raw;
}

function parseKeySignature(spec: string): KeySignature {
  const tokens = spec.trim().split("+");
  const sig: KeySignature = { mod: false, ctrl: false, alt: false, shift: false, key: "" };
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i] ?? "";
    if (i < tokens.length - 1) {
      if (MOD_TOKEN_RE.test(tok)) {
        sig.mod = true;
        continue;
      }
      if (CTRL_TOKEN_RE.test(tok)) {
        sig.ctrl = true;
        continue;
      }
      if (ALT_TOKEN_RE.test(tok)) {
        sig.alt = true;
        continue;
      }
      if (SHIFT_TOKEN_RE.test(tok)) {
        sig.shift = true;
        continue;
      }
    }
    sig.key = normalizeKeyToken(tok);
  }
  return sig;
}

/** Splits a sequence string ("g i") into per-step signatures. */
export function parseKeySequence(spec: string): KeySignature[] {
  return spec
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((step) => parseKeySignature(step));
}

/** True iff the given KeyboardEvent matches the signature on this platform. */
export function eventMatchesSignature(
  event: KeyboardEvent,
  sig: KeySignature,
  isMac: boolean,
): boolean {
  if (!isModSatisfied(event, sig, isMac)) {
    return false;
  }
  if (sig.alt !== event.altKey) {
    return false;
  }
  if (sig.shift !== event.shiftKey && !isShiftedPrintable(sig.key, event)) {
    return false;
  }
  return eventKeyEquals(event, sig.key);
}

function isModSatisfied(event: KeyboardEvent, sig: KeySignature, isMac: boolean): boolean {
  const metaPressed = event.metaKey;
  const ctrlPressed = event.ctrlKey;

  // Mod = Meta on macOS / Ctrl elsewhere
  const modOk = computeModOk(sig.mod, isMac, metaPressed, ctrlPressed);
  // If Mod is NOT required and we're on a Mac, Meta must be off (avoid swallowing Cmd+letters)
  const macMetaGuard = sig.mod || !isMac || !metaPressed;
  // Explicit Ctrl: required when sig.ctrl is true; otherwise no constraint.
  const ctrlOk = !sig.ctrl || ctrlPressed;

  return modOk && macMetaGuard && ctrlOk;
}

function computeModOk(
  required: boolean,
  isMac: boolean,
  metaPressed: boolean,
  ctrlPressed: boolean,
): boolean {
  if (required) {
    return isMac ? metaPressed : ctrlPressed;
  }
  return isMac ? !metaPressed : true;
}

function isShiftedPrintable(sigKey: string, event: KeyboardEvent): boolean {
  // If the signature key is a punctuation/shift-produced char (?, !, …) AND the
  // event already produced exactly that char, the shift state is implicit.
  if (sigKey.length !== 1) {
    return false;
  }
  return event.key === sigKey;
}

function eventKeyEquals(event: KeyboardEvent, key: string): boolean {
  if (key === "Space") {
    return event.key === " ";
  }
  if (key.length === 1) {
    return event.key.toLowerCase() === key;
  }
  return event.key === key;
}

function renderEventKey(event: KeyboardEvent): string {
  if (event.key === " ") {
    return "Space";
  }
  if (event.key.length === 1) {
    return event.key.toLowerCase();
  }
  return event.key;
}

/** Build a canonical signature string for an event, useful for debugging. */
export function eventToSignature(event: KeyboardEvent, isMac: boolean): string {
  const parts: string[] = [];
  const modPressed = isMac ? event.metaKey : event.ctrlKey;
  if (modPressed) {
    parts.push("Mod");
  }
  if (isMac && event.ctrlKey) {
    parts.push("Ctrl");
  }
  if (event.altKey) {
    parts.push("Alt");
  }
  if (event.shiftKey) {
    parts.push("Shift");
  }
  parts.push(renderEventKey(event));
  return parts.join("+");
}

/** Stable key used for binding lookup tables / tries. */
export function signatureKey(sig: KeySignature): string {
  const parts: string[] = [];
  if (sig.mod) {
    parts.push("Mod");
  }
  if (sig.ctrl) {
    parts.push("Ctrl");
  }
  if (sig.alt) {
    parts.push("Alt");
  }
  if (sig.shift) {
    parts.push("Shift");
  }
  parts.push(sig.key);
  return parts.join("+");
}
